import { useState } from 'react'
import type { ReactNode } from 'react'
import { ActionIcon, Badge, Group, List, SimpleGrid, Stack, Table, Text } from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import { EmptyState, HairlineDivider, MetaList, SectionCard } from '@lighthouse/design'
import { NOT_MEASURED, exactNumber, whenLabel } from '../../utils/format'
import { CostInputs } from './CostInputs'
import { TownCrierLink } from './TownCrierLink'
import { VerdictBadge } from './VerdictBadge'
import {
  atLabel, clockAtOffset, concurrencyLabel, countLabel, durationLabel, isFloorBucket, isReport, itemCostLabel,
  itemStateLabel, kindLabel,
  placementLabel, plural, ratioPercent, reasonText, slotSecondsLabel, timeLabel, timesLabel, verdictMeaning,
  wallClockIn,
} from './scheduleFormat'
import type { ScheduleLoadBucket, ScheduleLoadItem, ScheduleLoadSeries } from '../../types/scheduleLoad'

/**
 * One quarter-hour, opened: "what is in the 15:00 spike" (doc 11 §4, Diagnose).
 *
 * Laid out as the question is asked. The verdict and every condition that shaped
 * it; then the four groups of figures behind it — what starts, what is doubtful,
 * what is wanted, and what the pool can give; then every listed occurrence with
 * its own cost. Nothing here is a tooltip (S3).
 *
 * **Demand is a floor wherever something is unestimated**, because the server
 * gives an unknown occurrence no slot time. So the "wanted" figures carry a line
 * saying how many items they leave out, computed from the served counts — a
 * 200-report spike of unknowns shows near-zero demand, and that number must not
 * be readable on its own.
 */
export function BucketDetail({
  bucket, seriesByKey, liveSlots, bucketSeconds, workerIanaId, townCrierPath,
}: {
  bucket: ScheduleLoadBucket
  seriesByKey: Map<string, ScheduleLoadSeries>
  liveSlots: number | null
  bucketSeconds: number
  workerIanaId: string | null
  townCrierPath: string
}) {
  const doubtful = bucket.unestimatedStarts + bucket.floorCostStarts + bucket.unknownPossiblyRunning
  const available = liveSlots == null ? null : liveSlots * bucketSeconds

  return (
    <SectionCard
      title={`The quarter-hour from ${atLabel(bucket.start)}`}
      subtitle={`${timeLabel(bucket.startUtc)} UTC · every figure behind its verdict, and everything that starts in it`}
      action={<VerdictBadge verdict={bucket.verdict} />}
    >
      <Stack gap="md">
        <Stack gap={6}>
          <Text size="sm">{verdictMeaning(bucket.verdict)}</Text>
          {bucket.verdict === 'Indeterminate' && (
            <Group gap={6} align="center">
              <Text size="sm">The known demand alone:</Text>
              {/*
                FloorVerdict is Contended or null — never Clear. Null renders as a
                dash with words beside it, because "the known part does not
                contend" is not "clear": with 199 unknowns and one estimate,
                nothing has shown this quarter-hour clear.
              */}
              <VerdictBadge verdict={bucket.floorVerdict} />
              {bucket.floorVerdict == null && (
                <Text size="sm" c="dimmed">
                  the estimated part does not contend by itself, which is not the same as clear.
                </Text>
              )}
            </Group>
          )}
          {/*
            "Known work only", because that is what the server computes: both case
            verdicts ignore unknowns. Without the qualifier, "at the slowest
            observed: Clear" beside an Indeterminate headline would read as a
            second opinion that the quarter-hour is fine.
          */}
          <Group gap="lg">
            <Group gap={6}>
              <Text size="xs" c="dimmed">At median durations, known work only</Text>
              <VerdictBadge verdict={bucket.typicalCaseVerdict} />
            </Group>
            <Group gap={6}>
              <Text size="xs" c="dimmed">At the slowest observed, known work only</Text>
              <VerdictBadge verdict={bucket.worstCaseVerdict} />
            </Group>
          </Group>
          {bucket.verdictReasons.length > 0 && (
            <List size="sm" spacing={2}>
              {bucket.verdictReasons.map((code) => <List.Item key={code}>{reasonText(code)}</List.Item>)}
            </List>
          )}
        </Stack>

        <HairlineDivider />

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Figures
            title="What starts here"
            items={[
              { label: 'Starting in this quarter-hour', value: exactNumber(bucket.starts) },
              { label: 'With a cost estimate', value: exactNumber(bucket.estimatedStarts) },
              { label: 'With no estimate', value: exactNumber(bucket.unestimatedStarts) },
              { label: 'At a floor cost', value: exactNumber(bucket.floorCostStarts) },
              { label: 'Already delivered', value: exactNumber(bucket.completedStarts) },
              // Its own row: a failed delivery used to be counted as delivered here, because lastRunAt is
              // written on failure too. Its retry is counted among the starts of the quarter-hour it lands in,
              // costed like any occurrence of its schedule, and marked doubtful because when it ends is unknowable.
              { label: 'Failed, being retried', value: exactNumber(bucket.retryingStarts) },
              { label: 'Will refuse to start (ETL halted)', value: exactNumber(bucket.refusedStarts) },
              { label: 'Requested, not yet picked up', value: exactNumber(bucket.pendingTriggerStarts) },
            ]}
          />
          <Figures
            title="What this page cannot see"
            items={[
              {
                label: 'Unknown-cost work possibly still running',
                value: exactNumber(bucket.unknownPossiblyRunning),
              },
              {
                label: 'Series that may be missing firings here',
                value: exactNumber(bucket.incompleteSeriesCount),
              },
              {
                label: 'Jobs firing at an hour this page cannot compute',
                value: exactNumber(bucket.unplaceableWillFire),
              },
            ]}
          />
          <Figures
            title="What it wants, if everything started on time"
            note={doubtful > 0
              ? `A floor: ${plural(doubtful, 'item')} here or still running have no estimate or a floor cost, and add `
                + 'no slot time to these figures.'
              : undefined}
            items={[
              {
                label: 'Slot-seconds wanted',
                value: `typical ${slotSecondsLabel(bucket.typicalDemandSlotSeconds)} · planning `
                  + `${slotSecondsLabel(bucket.planningDemandSlotSeconds)} · worst `
                  + `${slotSecondsLabel(bucket.worstDemandSlotSeconds)}`,
              },
              {
                label: 'Slots wanted on average',
                value: `${concurrencyLabel(bucket.expectedConcurrency)} planning · `
                  + `${concurrencyLabel(bucket.typicalExpectedConcurrency)} typical`,
              },
              {
                label: 'Most wanting a slot at once',
                value: bucket.peakDemandAt == null
                  ? exactNumber(bucket.peakDemandConcurrency)
                  : `${exactNumber(bucket.peakDemandConcurrency)} at `
                    + `${clockAtOffset(bucket.peakDemandAt, bucket.displayOffsetMinutes)}`,
              },
            ]}
          />
          <Figures
            title="What the pool can give"
            note={liveSlots == null
              ? 'Capacity is unmeasured, so none of these is computed — they are dashes, not zeros.'
              // Not at a full pool: occupancy cannot exceed capacity, so 100% is exact.
              : isFloorBucket(bucket) && (bucket.utilization ?? 0) < 1
                ? 'Occupancy and utilization are floors here too, for the same reason: unestimated work holds no '
                  + 'slot time in the simulation.'
                : undefined}
            items={[
              { label: 'Live slots', value: countLabel(liveSlots) },
              {
                label: 'Slot-seconds available',
                value: available == null ? NOT_MEASURED : slotSecondsLabel(available),
              },
              {
                label: 'Slot-seconds occupied',
                value: `typical ${slotSecondsLabel(bucket.typicalOccupiedSlotSeconds)} · planning `
                  + `${slotSecondsLabel(bucket.planningOccupiedSlotSeconds)} · worst `
                  + `${slotSecondsLabel(bucket.worstOccupiedSlotSeconds)}`,
              },
              { label: 'Utilization (planning)', value: ratioPercent(bucket.utilization) },
              { label: 'Peak wanted against slots', value: timesLabel(bucket.peakRatio) },
              { label: 'Longest queue', value: countLabel(bucket.peakQueueLength) },
              {
                // Null when nothing STARTS here, even while an earlier burst is
                // still queued through it — the server measures the wait of the
                // items scheduled in this quarter-hour only.
                label: 'Longest wait, among those starting here',
                value: durationLabel(bucket.maxWaitSeconds),
              },
              { label: 'Still waiting when it ends', value: countLabel(bucket.backlogAtEnd) },
            ]}
          />
        </SimpleGrid>

        <HairlineDivider />

        <ItemsTable
          bucket={bucket}
          seriesByKey={seriesByKey}
          workerIanaId={workerIanaId}
          townCrierPath={townCrierPath}
        />
      </Stack>
    </SectionCard>
  )
}

function Figures({
  title, items, note,
}: {
  title: string
  items: { label: ReactNode; value: ReactNode }[]
  note?: string
}) {
  return (
    <Stack gap={6}>
      <Text size="sm" fw={600}>{title}</Text>
      {note && <Text size="xs" c="orange">{note}</Text>}
      <MetaList items={items} />
    </Stack>
  )
}

/**
 * Every listed occurrence starting in the quarter-hour.
 *
 * "What those reports are" — schedule, report, whose data scope it runs under —
 * and what each is expected to cost, with its series' full cost inputs one click
 * away. The server lists at most 100 per quarter-hour and budgets the response;
 * anything counted but not listed is said, with its count, never silently cut.
 */
function ItemsTable({
  bucket, seriesByKey, workerIanaId, townCrierPath,
}: {
  bucket: ScheduleLoadBucket
  seriesByKey: Map<string, ScheduleLoadSeries>
  workerIanaId: string | null
  townCrierPath: string
}) {
  const [expanded, setExpanded] = useState<string | null>(null)

  if (bucket.items.length === 0) {
    return bucket.itemsOmitted > 0 ? (
      <EmptyState
        title={`${plural(bucket.itemsOmitted, 'occurrence')} counted here, none listed`}
        message="The response lists occurrences from the earliest quarter-hour forward until its budget is spent. Narrow the window to see these."
      />
    ) : (
      <Text size="sm" c="dimmed">
        Nothing is scheduled to start in this quarter-hour. Its verdict comes from work that started earlier,
        or from what the page cannot see — the figures above say which.
      </Text>
    )
  }

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        Starting in this quarter-hour — {plural(bucket.items.length, 'occurrence')} listed
        {bucket.itemsOmitted > 0 && `, ${exactNumber(bucket.itemsOmitted)} more counted above but not listed`}
      </Text>
      <Table.ScrollContainer minWidth={960}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40} />
              <Table.Th>Starts</Table.Th>
              <Table.Th>What</Table.Th>
              <Table.Th>Runs as</Table.Th>
              <Table.Th>State</Table.Th>
              <Table.Th>Holds a slot for</Table.Th>
              {/*
                "Approx." because the server says so in its own assumptions:
                Hangfire dequeues with no ORDER BY, so WHICH item waits is a guess
                even though how many wait, and for how long in total, is not.
              */}
              <Table.Th>Waits (approx.)</Table.Th>
              <Table.Th>Change it</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {bucket.items.map((item, i) => {
              const key = `${item.seriesKey}|${item.startsAtUtc}|${i}`
              return (
                <ItemRow
                  key={key}
                  item={item}
                  series={seriesByKey.get(item.seriesKey)}
                  isExpanded={expanded === key}
                  onToggle={() => setExpanded(expanded === key ? null : key)}
                  workerIanaId={workerIanaId}
                  townCrierPath={townCrierPath}
                />
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  )
}

function ItemRow({
  item, series, isExpanded, onToggle, workerIanaId, townCrierPath,
}: {
  item: ScheduleLoadItem
  series: ScheduleLoadSeries | undefined
  isExpanded: boolean
  onToggle: () => void
  workerIanaId: string | null
  townCrierPath: string
}) {
  const report = isReport(item.kind)
  const unknown = item.costStatus === 'Unknown' || item.planningSeconds == null
  // For a worker job, what Automaton registered it as: the same instant in the
  // worker's zone. This is the line that explains a 02:30 job sitting at 19:30.
  const registered = report ? null : wallClockIn(item.startsAtUtc, workerIanaId)

  return (
    <>
      <Table.Tr style={{ cursor: 'pointer' }} onClick={onToggle}>
        <Table.Td>
          <ActionIcon
            variant="subtle"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} the cost inputs for ${item.name}`}
            onClick={(event) => {
              event.stopPropagation()
              onToggle()
            }}
          >
            {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          <Text size="sm">{timeLabel(item.startsAt)}</Text>
          {registered && <Text size="xs" c="dimmed">{registered} worker zone</Text>}
        </Table.Td>
        <Table.Td>
          <Text size="sm" fw={500}>{item.name}</Text>
          <Text size="xs" c="dimmed">
            {report ? (item.reportName ?? 'Report name not recorded') : kindLabel(item.kind)}
          </Text>
        </Table.Td>
        <Table.Td>
          {/* A job runs as nobody in particular; a report with no stamped name is unmeasured. */}
          {report
            ? <Text size="sm">{item.runAsDisplayName ?? NOT_MEASURED}</Text>
            : <Text size="sm" c="dimmed">Automaton</Text>}
        </Table.Td>
        <Table.Td>
          <Text size="sm">{itemStateLabel(item.state)}</Text>
          {item.completedAt != null && <Text size="xs" c="dimmed">finished {whenLabel(item.completedAt)}</Text>}
          {item.seriesTruncated && <Text size="xs" c="orange">series cut short — later firings missing</Text>}
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          {unknown ? (
            // A word and an outline, never a zero and never a dash: this is a cost
            // with no history to measure, not a measurement nobody took.
            <Badge variant="outline" color="gray" size="sm">Unknown</Badge>
          ) : (
            <>
              <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{itemCostLabel(item)} planning</Text>
              <Text size="xs" c="dimmed">{durationLabel(item.typicalSeconds)} typical</Text>
            </>
          )}
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {durationLabel(item.expectedWaitSeconds)}
        </Table.Td>
        <Table.Td onClick={(event) => event.stopPropagation()}>
          {report
            ? <TownCrierLink path={series?.townCrierPath ?? townCrierPath} scheduleName={item.name} compact />
            : <Text size="xs" c="dimmed">Automaton&rsquo;s own schedule</Text>}
        </Table.Td>
      </Table.Tr>

      {isExpanded && (
        <Table.Tr>
          <Table.Td colSpan={8} style={{ backgroundColor: 'var(--color-bg-inset, transparent)' }}>
            {series ? <SeriesInputs series={series} /> : (
              <Text size="sm" c="dimmed">
                This occurrence&rsquo;s series is not in the response, so its cost inputs cannot be shown.
              </Text>
            )}
          </Table.Td>
        </Table.Tr>
      )}
    </>
  )
}

/** A series' schedule and cost inputs, for an opened row. */
export function SeriesInputs({ series }: { series: ScheduleLoadSeries }) {
  return (
    <Stack gap="sm" py="sm">
      <MetaList
        items={[
          { label: 'Schedule', value: `${series.cronDescription} (${series.cronExpression ?? NOT_MEASURED})` },
          {
            label: 'In the zone',
            value: series.sourceZoneId == null
              ? NOT_MEASURED
              : `${series.sourceZoneId}${series.sourceZoneIanaId && series.sourceZoneIanaId !== series.sourceZoneId
                ? ` (${series.sourceZoneIanaId})` : ''}`,
          },
          { label: 'Placement', value: placementLabel(series.placement) },
          ...(series.placementDetail ? [{ label: 'Why', value: series.placementDetail }] : []),
          ...(series.reportStatus ? [{ label: 'Report status', value: series.reportStatus }] : []),
          {
            label: 'Last run',
            value: series.lastRunAt == null && series.lastRunStatus == null
              ? NOT_MEASURED
              : `${series.lastRunStatus ?? 'status not recorded'} · ${whenLabel(series.lastRunAt)}`,
          },
        ]}
      />
      <HairlineDivider />
      <CostInputs cost={series.cost} />
    </Stack>
  )
}
